/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */

import type { JSX } from "react";

import { useMemo } from "react";

import type { ColumnsType } from "antd/es/table";

import {
  CheckCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Popover, Select, Table, Typography } from "antd";

import { cn } from "@alaffia-technology-solutions/tailwind-utils";
import { formatDate } from "@alaffia-technology-solutions/utils";

import type { ClaimIntakeUpload } from "./util";

import { formatBytesAsDisplayString } from "../../common/util/fn/bytes";
import { toFirstLetterUpper } from "./util";

const { Text } = Typography;

const UploadClaimsTable = ({
  uploadFiles,
  isShowingHistory,
  documentUploadKindOptions,
  setUploadKind,
  removeUpload,
}: {
  uploadFiles: ClaimIntakeUpload[];
  isShowingHistory?: boolean;
  documentUploadKindOptions: { label: string; value: string }[];
  setUploadKind: (upload: ClaimIntakeUpload, kind: string) => void;
  removeUpload?: (upload: ClaimIntakeUpload) => void;
}): JSX.Element => {
  const getKindLabelForOption = (documentUploadKindOption?: string) =>
    !documentUploadKindOption
      ? ""
      : (documentUploadKindOptions.find(
          (ukOpt) => ukOpt.value === documentUploadKindOption,
        )?.label ?? documentUploadKindOption);

  const columns: ColumnsType<ClaimIntakeUpload> = useMemo(
    () => [
      {
        title: "File",
        dataIndex: ["uploadFile", "name"],
        width: "33%",
        ellipsis: true,
        render: (name, record) => (
          <Popover
            content={
              <>
                <div>
                  <Text type="secondary">File:</Text> {name}
                </div>
                <div>
                  <Text type="secondary">Claim Number:</Text>{" "}
                  {record.metadata.claimNumber}
                </div>
                <div>
                  <Text type="secondary">Insurer:</Text>{" "}
                  {record.metadata.insurerName}
                </div>
              </>
            }
            title="File Info"
          >
            <div>
              <Text>{name}</Text>
            </div>
            <div>
              <Text type="secondary" className="af-text-[.85em]">
                Claim: {record.metadata.claimNumber}
              </Text>
            </div>
            <div>
              <Text type="secondary" className="af-text-[.85em]">
                Insurer: {record.metadata.insurerName}
              </Text>
            </div>
          </Popover>
        ),
      },
      {
        title: "Size",
        width: "10%",
        dataIndex: ["uploadFile", "size"],
        responsive: ["xxl", "xl", "lg"],
        render: (size) => (
          <Popover content={size}>
            <Text className="af-text-[.85em]">
              {formatBytesAsDisplayString(size, 1)}
            </Text>
          </Popover>
        ),
      },
      {
        title: "Uploaded",
        dataIndex: ["metadata", "uploadedAt"],
        width: "12%",
        responsive: ["xxl", "xl", "lg"],
        render: (_ua, record) => (
          <Popover
            content={
              record.metadata.uploadedAt
                ? formatDate({
                    date: record.metadata.uploadedAt,
                    format: "date-time",
                  })
                : undefined
            }
          >
            <Text className="af-text-[.85em]">
              {record.metadata.uploadedAt
                ? formatDate({
                    date: record.metadata.uploadedAt,
                    format: "time",
                  })
                : undefined}
            </Text>
          </Popover>
        ),
      },
      {
        title: "Status",
        dataIndex: ["metadata", "status"],
        width: "16%",
        ellipsis: true,
        render: (status, record) => (
          <Popover
            title={toFirstLetterUpper(status)}
            content={
              <div>
                <div>
                  <Text>{record.metadata.statusMessage}</Text>
                </div>
                {record.metadata.status === "success" && (
                  <div>
                    <Text type="secondary" className="af-text-[.85em]">
                      Uploaded:{" "}
                      {record.metadata.uploadedAt
                        ? formatDate({
                            date: record.metadata.uploadedAt,
                            format: "time",
                          })
                        : undefined}
                    </Text>
                  </div>
                )}
                <div>
                  <Text type="secondary" className="af-text-[.85em]">
                    Size: {record.uploadFile.size}
                  </Text>
                </div>
              </div>
            }
          >
            {status === "success" ? (
              <CheckCircleOutlined className="af-text-[1.1em] af-text-[#52c41a]" />
            ) : status === "ready" ? (
              <UploadOutlined className="af-text-[1.1em] af-text-[gray]" />
            ) : status === "pending" ? (
              <SyncOutlined
                spin
                className="af-text-[1.1em] af-text-[#1890ff]"
              />
            ) : status === "error" ? (
              <ExclamationCircleOutlined className="af-text-[1.1em] af-text-[#ff4d4f]" />
            ) : undefined}
            <Text
              type={
                status === "success"
                  ? "success"
                  : status === "error"
                    ? "danger"
                    : undefined
              }
            >
              {" " + toFirstLetterUpper(status)}
            </Text>
            {record.metadata.statusMessage && (
              <div>
                <Text
                  ellipsis={true}
                  type="secondary"
                  className="af-text-[.85em]"
                >
                  {record.metadata.statusMessage}
                </Text>
              </div>
            )}
          </Popover>
        ),
      },
      {
        title: "Kind",
        dataIndex: ["metadata", "documentUploadKind"],
        width: "25%",
        render: (documentUploadKind, upload) => (
          <>
            {(isShowingHistory ?? upload.metadata.status === "success") ? (
              getKindLabelForOption(documentUploadKind)
            ) : (
              <div
                className={cn(
                  (!documentUploadKind ||
                    documentUploadKind.length < 1 ||
                    upload.metadata.duplicateUB === true ||
                    upload.metadata.duplicateNameAndKind === true) &&
                    "[&_div.ant-select-selector:not(:hover):not(:focus-within)]:![border:solid_#ff4d4f_1px]",
                )}
              >
                <Select
                  data-id="document-type-select"
                  placeholder="Select document type"
                  size="small"
                  listItemHeight={22}
                  popupMatchSelectWidth={false}
                  options={documentUploadKindOptions}
                  virtual={false}
                  value={documentUploadKind}
                  onSelect={(newDocumentUploadKind) => {
                    // Set the documentUploadKind for this file in the list
                    setUploadKind(upload, newDocumentUploadKind);
                  }}
                />
              </div>
            )}
          </>
        ),
      },
      {
        ...(removeUpload && {
          title: "",
          width: "4%",
          dataIndex: ["uploadFile", "uid"],
          render: (_uid, record) => {
            const disabled = record.metadata.status === "pending";
            return (
              <Popover content="Remove entry from active list">
                <DeleteOutlined
                  style={{
                    padding: 0,
                    margin: 0,
                    color: disabled ? "lightgrey" : undefined,
                  }}
                  onClick={() => !disabled && removeUpload(record)}
                />
              </Popover>
            );
          },
        }),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <div className="af-m-0 af-flex af-h-full af-w-full af-flex-col af-p-0">
      <div className="af-m-0 af-min-w-[600px] af-flex-auto af-p-0 [&_.ant-table-thead_>_tr_>_th]:af-bg-[unset] [&_.ant-table-thead_>_tr_>_th]:af-p-[8px] [&_.ant-table-thead_>_tr_>_th]:af-text-[.95em]">
        {isShowingHistory && (
          <Text type="secondary" className="af-text-[.85em]">
            ** History is only available for the current browser session,
            reloading the browser will clear history.
          </Text>
        )}
        <Table
          rowKey={(row) => row.uploadFile.uid}
          columns={columns}
          dataSource={uploadFiles}
          pagination={false}
        />
      </div>
    </div>
  );
};

export default UploadClaimsTable;
